<template>
  <main class="mine-kyc-page">
    <AppNavBar :title="t('mine.kyc')" />

    <van-skeleton v-if="loading" title :row="6" class="loading-card" />

    <template v-else>
      <section class="status-card">
        <div class="status-card__head">
          <div class="status-card__title">
            <app-image :src="kycIcon" alt="" />
            <div data-strong>{{ t('mine.kyc') }}</div>
          </div>
          <span class="status-badge" :class="statusMeta.className">
            <app-image v-if="statusMeta.icon" :src="statusMeta.icon" alt="" />
            {{ statusMeta.label }}
          </span>
        </div>

        <p class="status-card__desc">{{ statusMeta.desc }}</p>

        <div class="info-list">
          <div class="info-row">
            <span>{{ t('card.cardholder') }}</span>
            <div data-strong>{{ holderName }}</div>
          </div>
          <div class="info-row">
            <span>{{ t('card.phoneNumberLabel') }}</span>
            <div data-strong>{{ phoneText }}</div>
          </div>
          <div class="info-row">
            <span>{{ t('card.address') }}</span>
            <div data-strong>{{ addressText }}</div>
          </div>
        </div>

        <div v-if="rejectReason" class="reason-box">
          <div data-strong>{{ t('card.failedReason') }}</div>
          <p>{{ rejectReason }}</p>
        </div>
      </section>

      <section class="notice-card">
        <h2>{{ t('card.kycRequirement') }}</h2>
        <div class="notice-item">
          <i />
          <p>{{ t('card.kycRequirement1') }}</p>
        </div>
        <div class="notice-item">
          <i />
          <p>{{ t('card.kycRequirement2') }}</p>
        </div>
      </section>

      <div class="action-bar">
        <van-button block round type="primary" @click="goNext">
          {{ statusMeta.button }}
        </van-button>
      </div>
    </template>
  </main>
</template>

<script setup>
import { computed, onMounted, ref } from 'vue';
import { showFailToast } from 'vant';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';
import { getUserKyc, pcCardCreatePre } from '@/api/card';
import { useProfileStore } from '@/stores/profile';
import { navigateWithState } from '@/utils/route-state';
import kycIcon from '@/assets/images/settingImage/setting_list_3.png';
import yesIcon from '@/assets/images/circle_yes_icon.png';

const { t } = useI18n();
const router = useRouter();
const profileStore = useProfileStore();

const loading = ref(false);
const preInfo = ref({});
const kycInfo = ref({});

const holderStatus = computed(() => Number(preInfo.value?.holderStatus || 1));
const holderName = computed(() => {
  const firstName = preInfo.value?.firstName || '';
  const lastName = preInfo.value?.lastName || '';
  return `${lastName} ${firstName}`.trim() || '--';
});
const phoneText = computed(() => {
  const code = kycInfo.value?.phoneCountryCode ? `+${kycInfo.value.phoneCountryCode}` : '';
  const phone = kycInfo.value?.phoneNumber || '';
  return `${code} ${phone}`.trim() || '--';
});
const addressText = computed(() => {
  const values = [
    kycInfo.value?.country,
    kycInfo.value?.state,
    kycInfo.value?.city,
    kycInfo.value?.addressLine1
  ].filter(Boolean);
  return values.join(' ') || '--';
});
const rejectReason = computed(() => String(kycInfo.value?.reason || ''));

const statusMeta = computed(() => {
  if (holderStatus.value === 3) {
    return {
      label: t('mine.kycVerified'),
      desc: t('mine.kycVerifiedDesc'),
      button: t('mine.viewKycResult'),
      className: 'status-badge--success',
      icon: yesIcon
    };
  }
  if (holderStatus.value === 2) {
    return {
      label: t('mine.kycPending'),
      desc: t('mine.kycPendingDesc'),
      button: t('card.checkVerificationStatus'),
      className: 'status-badge--pending',
      icon: ''
    };
  }
  if (holderStatus.value === 4) {
    return {
      label: t('mine.kycFailed'),
      desc: t('mine.kycFailedDesc'),
      button: t('mine.retryKyc'),
      className: 'status-badge--failed',
      icon: ''
    };
  }
  return {
    label: t('mine.kycTodo'),
    desc: t('mine.kycTodoDesc'),
    button: t('card.verifyNow'),
    className: 'status-badge--todo',
    icon: ''
  };
});

async function loadData() {
  if (!profileStore.platformAddress) {
    router.replace('/login');
    return;
  }

  loading.value = true;
  try {
    const [preResult, kycResult] = await Promise.allSettled([
      pcCardCreatePre({ address: profileStore.platformAddress }),
      getUserKyc({ address: profileStore.platformAddress })
    ]);

    preInfo.value = preResult.status === 'fulfilled' ? (preResult.value || {}) : {};
    kycInfo.value = kycResult.status === 'fulfilled' ? (kycResult.value || {}) : {};
  } catch (error) {
    showFailToast(error.msg || t('common.retryLater'));
  } finally {
    loading.value = false;
  }
}

function goNext() {
  if (holderStatus.value === 3) {
    navigateWithState(router, '/card/kyc/result', {
      namespace: 'card-kyc-result',
      payload: {
        holderStatus: '3',
        reason: rejectReason.value
      }
    });
    return;
  }
  if (holderStatus.value === 2 || holderStatus.value === 4 || (holderStatus.value === 1 && rejectReason.value)) {
    navigateWithState(router, '/card/kyc/result', {
      namespace: 'card-kyc-result',
      payload: {
        holderStatus: String(holderStatus.value || 1),
        reason: rejectReason.value
      }
    });
    return;
  }
  router.push('/card/kyc');
}

onMounted(loadData);
</script>

<style scoped lang="scss">
.mine-kyc-page {
  min-height: var(--app-viewport-height);
  padding: 14px 16px 36px;
  background: #f7f8fa;
}

.loading-card,
.status-card,
.notice-card {
  border-radius: 14px;
  background: #ffffff;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.04);
}

.status-card {
  padding: 18px 16px;

  &__head {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
  }

  &__title {
    display: flex;
    align-items: center;
    gap: 10px;

    .app-image {
      width: 24px;
      height: 24px;
    }

    [data-strong] {
      color: #222222;
      font-size: 18px;
      font-weight: 500;
    }
  }

  &__desc {
    margin: 14px 0 0;
    color: #666666;
    font-size: 14px;
    line-height: 22px;
  }
}

.status-badge {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 6px 10px;
  border-radius: 14px;
  font-size: 12px;
  line-height: 1;

  .app-image {
    width: 14px;
    height: 14px;
  }

  &--todo {
    color: #ef0027;
    background: #ffdae0;
  }

  &--pending {
    color: #ff8800;
    background: rgba(255, 231, 161, 0.55);
  }

  &--success {
    color: #07b077;
    background: rgba(194, 255, 234, 0.6);
  }

  &--failed {
    color: #ef0027;
    background: #ffdae0;
  }
}

.info-list {
  margin-top: 18px;
}

.info-row {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 14px;
  padding: 14px 0;
  border-bottom: 1px solid #f4f5f6;

  &:last-child {
    border-bottom: 0;
    padding-bottom: 0;
  }

  span {
    flex: 0 0 78px;
    color: #999999;
    font-size: 14px;
  }

  [data-strong] {
    flex: 1;
    color: #222222;
    font-size: 14px;
    line-height: 22px;
    text-align: right;
    word-break: break-word;
  }
}

.reason-box {
  margin-top: 18px;
  padding: 14px;
  border-radius: 12px;
  background: #f2f7ff;
  border: 1px solid #cadeff;

  [data-strong] {
    display: block;
    color: #2c6bff;
    font-size: 14px;
    font-weight: 500;
  }

  p {
    margin: 8px 0 0;
    color: #4a5568;
    font-size: 13px;
    line-height: 20px;
  }
}

.notice-card {
  margin-top: 12px;
  padding: 18px 16px;

  h2 {
    margin: 0;
    color: #222222;
    font-size: 16px;
    font-weight: 500;
  }
}

.notice-item {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  margin-top: 14px;

  i {
    width: 8px;
    height: 8px;
    margin-top: 7px;
    border-radius: 50%;
    background: #2c6bff;
  }

  p {
    margin: 0;
    color: #666666;
    font-size: 14px;
    line-height: 22px;
  }
}

.action-bar {
  margin-top: 28px;
}
</style>
